import React, { Children, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import BaseTab from './Tab'
import { Label, Labels, Panels } from './Tabs.styles'

export const Tab = BaseTab

/**
 * The Tabs' component.
 */
const Tabs = props => {
  const { accessibility, alignment, children, margin, padding, value, variant, onChange, ...restProps } = props
  const labelsRef = useRef(undefined)
  const [tabs, setTabs] = useState(new window.Map())
  const [selectedTab, setSelectedTab] = useState(value)
  const handleChange = useCallback(
    (_, updatedValue) => {
      setSelectedTab(updatedValue)
      onChange(updatedValue)
    },
    [onChange]
  )
  const handleRegister = useCallback(tab => setTabs(customTabs => new window.Map(customTabs.set(tab.id, tab))), [])
  const panels = useMemo(
    () => Children.map(children, child => cloneElement(child, { value: selectedTab, onRegister: handleRegister })),
    [children, handleRegister, selectedTab]
  )

  useEffect(() => {
    const element = document.createElement('hr')

    element.className = 'MuiTabs-borderBottom'

    labelsRef.current.appendChild(element)
  }, [])

  useEffect(() => {
    setSelectedTab(value)
  }, [value])

  return (
    <>
      <Labels
        {...restProps}
        alignment={alignment}
        aria-label={accessibility.label}
        aria-labelledby={accessibility.labelledby}
        ref={labelsRef}
        scrollButtons="auto"
        value={selectedTab}
        variant={variant}
        onChange={handleChange}
      >
        {[...tabs.keys()].map(key => {
          const tab = tabs.get(key)

          return (
            <Label
              disableRipple
              disableFocusRipple
              wrapped
              disabled={tab.disabled && tab.id !== selectedTab}
              key={`tab-label-${tab.id}`}
              label={tab.label}
              value={tab.id}
            />
          )
        })}
      </Labels>
      <Panels margin={margin} padding={padding}>
        {panels}
      </Panels>
    </>
  )
}

Tabs.defaultProps = {
  accessibility: { label: 'table', labelledby: '' },
  alignment: 'left',
  margin: 0,
  padding: '34px 0',
  variant: 'scrollable',
  onChange: () => undefined
}

Tabs.propTypes = {
  /**
   * Accessibility.
   */
  accessibility: PropTypes.shape({
    label: PropTypes.string,
    labelledby: PropTypes.string
  }),
  /**
   * Alignment.
   */
  alignment: PropTypes.oneOf(['center', 'left', 'right']),
  /**
   * Children.
   */
  children: PropTypes.node.isRequired,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Value.
   */
  value: PropTypes.string.isRequired,
  /**
   * Variant.
   */
  variant: PropTypes.oneOf(['fullWidth', 'scrollable', 'standard']),
  /**
   * On change.
   */
  onChange: PropTypes.func
}

export default Tabs
